import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Hangar } from "../../../types";
import mixpanel from "../../../utils/mixpanel";
import { EstimatedValueBreakdown } from "../HangarsPresenter";
import { UtilizationDisplay } from "./UtilizationDisplay";

interface HangarRowProps {
  hangar: Hangar;
  aircraft: string;
  aircraftInside: number;
  aircraftTotal: number;
  utilization: string;
  utilizationStacked: number;
  utilizationAll: number;
  estimatedValueBreakdown?: EstimatedValueBreakdown;
  estimatedTowTime: string;
  lastStacked: Date | null;
  displayEditTenants: boolean;
  enableScheduling: boolean;
  isTablet: boolean;
  displayEstimatedValue: boolean;
  isSandbox?: boolean;
  sandboxDisplayName?: string;
}

export const HangarRow: React.FC<HangarRowProps> = ({
  hangar,
  aircraftInside,
  aircraftTotal,
  utilizationStacked,
  utilizationAll,
  estimatedValueBreakdown,
  estimatedTowTime,
  lastStacked,
  displayEditTenants,
  enableScheduling,
  isTablet,
  displayEstimatedValue,
  isSandbox = false,
  sandboxDisplayName,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const gridColumns = isTablet
    ? "200px 160px 180px 120px 1fr"
    : displayEstimatedValue
    ? "200px 160px 180px 120px 140px 120px 1fr"
    : "200px 160px 180px 140px 120px 1fr";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "grey.200",
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: "action.hover",
        },
        ...(isSandbox && {
          bgcolor: "action.selected",
          borderLeft: "3px solid",
          borderLeftColor: "warning.main",
        }),
      }}
    >
      {/* Hangar Name */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {sandboxDisplayName || hangar.name}
        </Typography>
        {isSandbox && (
          <Chip
            label={t("common.sandbox.badge")}
            size="small"
            color="warning"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        )}
      </Stack>

      {/* Aircraft */}
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "right" }}>
        {t("screens.hangars.hangarRow.aircraftCount", { inside: aircraftInside, total: aircraftTotal })}
      </Typography>

      {/* Utilization */}
      <UtilizationDisplay
        stackedUtilization={utilizationStacked}
        allUtilization={utilizationAll}
      />

      {/* Est. Value (desktop only) */}
      {!isTablet && displayEstimatedValue && estimatedValueBreakdown && (
        <Tooltip
          title={t("screens.hangars.hangarsPresenter.tooltips.estValueBreakdown", {
            base: estimatedValueBreakdown.baseValue,
            transient: estimatedValueBreakdown.transientValue,
          })}
          arrow
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "right", cursor: "help" }}
          >
            {estimatedValueBreakdown.formattedTotal}
          </Typography>
        </Tooltip>
      )}

      {/* Est. Tow Time (desktop only) */}
      {!isTablet && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "right" }}
        >
          {estimatedTowTime}
        </Typography>
      )}

      {/* Last Stacked */}
      <Tooltip
        title={lastStacked ? lastStacked.toISOString() : t("screens.hangars.hangarRow.lastStacked.tooltip")}
        arrow
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 400, textAlign: "right" }}
        >
          {lastStacked ? format(lastStacked, "MMM d") : t("screens.hangars.hangarRow.lastStacked.never")}
        </Typography>
      </Tooltip>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ flexWrap: "nowrap" }}
      >
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => {
            mixpanel.track("Clicked Hangar", {
              location_name: hangar.name,
              location_type: "hangar",
            });
            navigate(`/hangars/${hangar.id}`);
          }}
          sx={{
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            px: 1.5,
            py: 0.4,
            fontSize: "0.75rem",
            borderRadius: "14px",
          }}
        >
          {t("screens.hangars.hangarRow.buttons.currentHangar")}
        </Button>
        {enableScheduling && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => {
              mixpanel.track("Clicked Hangar Schedule", {
                location_name: hangar.name,
                location_type: "hangar",
              });
              navigate(`/hangars/${hangar.id}/schedule`);
            }}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              px: 1.5,
              py: 0.4,
              fontSize: "0.75rem",
              borderRadius: "14px",
            }}
          >
            {t("screens.hangars.hangarRow.buttons.hangarSchedule")}
          </Button>
        )}
        {displayEditTenants && (
          <Button
            size="small"
            variant="outlined"
            sx={{
              color: "text.primary",
              borderColor: "grey.300",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              px: 1.5,
              py: 0.4,
              fontSize: "0.75rem",
              borderRadius: "14px",
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: "grey.50",
              },
            }}
            onClick={() => {
              navigate(`/reference-hangars/${hangar.id}`);
            }}
          >
            {t("screens.hangars.hangarRow.buttons.editTenants")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
